import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { clearPersistedPageViewState } from '../utils/pageViewState';
import { getUserData } from '../utils/userUtils';
import { isAdmin, isSuperAdmin } from '../utils/permissions';
import { api, getApiUrl } from '../utils/apiConfig';

// Create the context
const AccountContext = createContext();

/**
 * Provider component for account selection context
 */
export const AccountProvider = ({ children }) => {
  // Initialize selectedAccountId from localStorage if available
  const [selectedAccountId, setSelectedAccountId] = useState(() => {
    try {
      return localStorage.getItem('selectedAccountId') || null;
    } catch (e) {
      console.error('Error reading selectedAccountId from localStorage:', e);
      return null;
    }
  });
  const [accounts, setAccounts] = useState([]);
  // Tracks whether the admin-accounts fetch has completed at least once.
  // Without this, the stale-id cleanup below can't tell "still loading"
  // from "loaded but empty" — and a user with zero admin accounts would
  // keep using whatever's in localStorage forever (e.g. after a partial
  // promotion that set the global Admin role but not is_account_admin).
  const [accountsFetched, setAccountsFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const isFetchingAccountsRef = useRef(false);

  // Name of the currently-selected account. Populated from a per-account
  // endpoint that accepts any account member — unlike `accounts[]`, which
  // is only fetched for regular admins. UI gates that need to identify an
  // account by name (e.g. "GroundTruth" / "Adara" for Review CTS) should
  // consult `currentAccountName` so they work for every role.
  const [currentAccountName, setCurrentAccountName] = useState(null);
  const fetchedNameForRef = useRef(null);

  // Initialize user data and fetch accounts
  useEffect(() => {
    const userData = getUserData();
    if (userData && userData.userId) {
      setUser(userData);

      // We'll handle account fetching in the user effect below
      // Don't call fetchAccountsForUser here to avoid duplicate calls
    }
  }, []);

  // Helper function to fetch accounts for a specific user
  const fetchAccountsForUser = async (userData) => {
    if (!userData) {
      console.log('No user data provided to fetchAccountsForUser');
      return;
    }

    // Only fetch accounts for regular admins (not super admins)
    if (!isAdmin(userData) || isSuperAdmin(userData)) {
      console.log('User is not a regular admin or is a super admin, skipping account fetch');
      // If not a regular admin, unblock chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = false;
      }
      return;
    }

    // Prevent duplicate requests
    if (isFetchingAccountsRef.current) {
      console.log('Already fetching accounts, skipping duplicate request');
      return;
    }

    console.log('Starting account fetch for specific user:', userData.userId || userData.id);

    try {
      isFetchingAccountsRef.current = true;
      setIsLoading(true);

      // Make sure we have a valid userId
      const userId = userData.userId || userData.id;

      if (!userId) {
        console.error('No valid userId found in userData:', userData);
        throw new Error('No valid userId found');
      }

      // Fetch accounts where the user is an account admin
      const response = await api.get(getApiUrl('/api/account-admin/accounts'), {
        params: { userId }
      });

      console.log('Accounts fetched successfully for specific user:', response.data);

      // Set the accounts in state
      setAccounts(response.data);
      setAccountsFetched(true);

      // After accounts are loaded, dispatch an event regardless of selectedAccountId
      // This allows components to react to account loading completion
      console.log('Accounts loaded for specific user, dispatching accountsLoaded event');

      // Use a small timeout to ensure state updates have completed
      setTimeout(() => {
        const accountsLoadedEvent = new CustomEvent('accountsLoaded', {
          detail: {
            selectedAccountId,
            accounts: response.data,
            timestamp: Date.now(), // Add timestamp to make each event unique
            source: 'fetchAccountsForUser'
          }
        });
        window.dispatchEvent(accountsLoadedEvent);

        // Only unblock chat history loading if we have a selected account
        if (selectedAccountId && window.blockInitialChatHistoryCall !== undefined) {
          console.log('Selected account exists, unblocking chat history loading');
          window.blockInitialChatHistoryCall = false;
        }
      }, 50);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      // In case of error, unblock chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = false;
      }
    } finally {
      setIsLoading(false);
      isFetchingAccountsRef.current = false;
    }
  };

  // Add a function to detect page refreshes
  const detectPageRefresh = useCallback(() => {
    // Set a flag in sessionStorage when the page loads
    const pageLoadCount = sessionStorage.getItem('pageLoadCount');
    if (!pageLoadCount) {
      sessionStorage.setItem('pageLoadCount', '1');
      return false; // First load
    }

    // Increment the page load count
    const count = parseInt(pageLoadCount, 10) + 1;
    sessionStorage.setItem('pageLoadCount', count.toString());
    return true; // Page refresh
  }, []);

  // We no longer clear account selection on page load/refresh
  // Instead, we persist it in localStorage and restore it on page load
  useEffect(() => {
    // If we detect a page refresh, we'll log it but not clear the selectedAccountId
    if (detectPageRefresh()) {
      console.log('Page refreshed, selectedAccountId preserved:', selectedAccountId);
    }
  }, [detectPageRefresh, selectedAccountId]);

  // Auto-select when the user has exactly one accessible account — no point
  // forcing a manual pick from a list of one, and lets the sidebar hide the
  // selector entirely. Runs after accountsFetched=true so it doesn't fire
  // mid-load, and bails if a (still-valid) selection is already set.
  useEffect(() => {
    if (!user) return;
    if (!isAdmin(user) || isSuperAdmin(user)) return;
    if (!accountsFetched) return;
    if (selectedAccountId) return;
    if (accounts.length !== 1) return;
    selectAccount(accounts[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, accountsFetched, selectedAccountId, user]);

  // Drop a stale selectedAccountId when it isn't in the user's accessible
  // accounts. Without this, a value left in localStorage from a prior session
  // (e.g. impersonation, account-membership change) keeps getting sent to the
  // API and yields 403 "Access denied to this account".
  useEffect(() => {
    if (!selectedAccountId || !user) return;
    // accounts[] is only populated for regular admins; skip other roles.
    if (!isAdmin(user) || isSuperAdmin(user)) return;
    // Wait until accounts have actually been fetched before validating.
    // We can't gate on accounts.length here — an empty list is a legitimate
    // "loaded" state (user has zero admin accounts) and must also clear the
    // stale id, otherwise the frontend keeps banging on a non-member account.
    if (!accountsFetched) return;
    if (accounts.some(a => a.id === selectedAccountId)) return;

    console.warn('Stored selectedAccountId not in user accounts; clearing', selectedAccountId);
    setSelectedAccountId(null);
    try {
      localStorage.removeItem('selectedAccountId');
    } catch (e) {
      console.error('Error clearing selectedAccountId from localStorage:', e);
    }
  }, [accounts, accountsFetched, selectedAccountId, user]);

  // Fetch accounts for the user
  const fetchAccounts = useCallback(async () => {
    // Get the current user data
    const userData = user || getUserData();

    // Only fetch accounts for regular admins (not super admins)
    if (!userData) {
      console.log('No user data available, skipping account fetch');
      return;
    }

    if (!isAdmin(userData)) {
      console.log('User is not an admin, skipping account fetch');
      return;
    }

    if (isSuperAdmin(userData)) {
      console.log('User is a super admin, skipping account fetch');
      return;
    }

    // Prevent duplicate requests
    if (isFetchingAccountsRef.current) {
      console.log('Already fetching accounts, skipping duplicate request');
      return;
    }

    console.log('Starting account fetch for user:', userData.userId || userData.id);

    try {
      isFetchingAccountsRef.current = true;
      setIsLoading(true);

      const userId = userData.userId || userData.id;
      if (!userId) {
        console.error('No valid userId found in userData:', userData);
        return;
      }

      // Fetch accounts where the user is an account admin
      const response = await api.get(getApiUrl('/api/account-admin/accounts'), {
        params: { userId }
      });

      console.log('Accounts fetched successfully:', response.data);

      // Set the accounts in state
      setAccounts(response.data);
      setAccountsFetched(true);

      // After accounts are loaded, dispatch an event regardless of selectedAccountId
      // This allows components to react to account loading completion
      console.log('Accounts loaded, dispatching accountsLoaded event');

      // Use a small timeout to ensure state updates have completed
      setTimeout(() => {
        const accountsLoadedEvent = new CustomEvent('accountsLoaded', {
          detail: {
            selectedAccountId,
            accounts: response.data,
            timestamp: Date.now() // Add timestamp to make each event unique
          }
        });
        window.dispatchEvent(accountsLoadedEvent);

        // Only unblock chat history loading if we have a selected account
        if (selectedAccountId && window.blockInitialChatHistoryCall !== undefined) {
          console.log('Selected account exists, unblocking chat history loading');
          window.blockInitialChatHistoryCall = false;
        }
      }, 50);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      // In case of error, unblock chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = false;
      }
    } finally {
      setIsLoading(false);
      isFetchingAccountsRef.current = false;
    }
  }, [user, selectedAccountId]);

  // Select an account
  const selectAccount = useCallback((accountId) => {
    console.log('Selecting account:', accountId);

    // Read the OUTGOING account before localStorage is overwritten below.
    // Deliberately not `selectedAccountId` from state: that would put a
    // changing value in this callback's dependency list and re-create it on
    // every switch, for a value localStorage already holds.
    const previousAccountId = (() => {
      try {
        return localStorage.getItem('selectedAccountId');
      } catch (e) {
        return null;
      }
    })();

    // Update the state
    setSelectedAccountId(accountId);

    // Each page remembers its own page number, sort, search and filters in
    // sessionStorage, keyed by PAGE and not by account — so without this the
    // outgoing account's view follows you in. The visible symptom is a page
    // number that no longer exists: page 4 of a large account, applied to one
    // with five folders, asks for rows 31-40 and renders an empty list beside
    // a count badge that says 5.
    //
    // Only on an actual change: re-selecting the current account (a repeated
    // click, a re-render) must not throw away where the user was.
    if (previousAccountId && previousAccountId !== accountId) {
      clearPersistedPageViewState();
    }

    try {
      // Store the selected account ID in localStorage to persist across page refreshes
      if (accountId) {
        localStorage.setItem('selectedAccountId', accountId);
      } else {
        localStorage.removeItem('selectedAccountId');
      }

      // Dispatch a custom event to notify other components about the account change
      const accountChangeEvent = new CustomEvent('accountChange', {
        detail: {
          accountId,
          timestamp: Date.now() // Add timestamp to make each event unique
        }
      });
      window.dispatchEvent(accountChangeEvent);
    } catch (e) {
      console.error('Error in selectAccount:', e);
    }
  }, []);

  // Clear selected account
  const clearSelectedAccount = useCallback(() => {
    setSelectedAccountId(null);
    try {
      // Remove from localStorage
      localStorage.removeItem('selectedAccountId');

      // Dispatch a custom event to notify other components
      const accountChangeEvent = new CustomEvent('accountChange', {
        detail: { accountId: null }
      });
      window.dispatchEvent(accountChangeEvent);
    } catch (e) {
      console.error('Error in clearSelectedAccount:', e);
    }
  }, []);

  // Get the current account ID to use for all operations
  // This is the single source of truth for the accountId
  const getAccountIdToUse = useCallback(() => {
    if (!user) return null;

    let accountIdToUse;
    const userData = getUserData();

    // For regular admins, use the selected account ID
    if (isAdmin(userData) && !isSuperAdmin(userData)) {
      // Only use the selected account ID, don't fall back to default
      accountIdToUse = selectedAccountId;
    } else {
      // For super admins and regular users, use the accountId from the user object
      accountIdToUse = userData.accountId;
    }

    // For impersonated users who are regular admins, always use selectedAccountId
    if (userData.isImpersonating && isAdmin(userData) && !isSuperAdmin(userData)) {
      accountIdToUse = selectedAccountId;

      // Log the account ID being used for impersonated users
      console.log('Impersonated user using accountId:', accountIdToUse);
    }

    // For impersonated users who are not regular admins, use their accountId
    if (userData.isImpersonating && (!isAdmin(userData) || isSuperAdmin(userData))) {
      // Try to get accountId from different properties
      accountIdToUse = userData.accountId || userData.account_id;
      console.log('Impersonated non-admin or super admin using accountId:', accountIdToUse);
    }

    // If still no accountId, try to get it from the user object directly
    if (!accountIdToUse && user) {
      accountIdToUse = user.accountId || user.account_id;
      console.log('Falling back to user object accountId:', accountIdToUse);
    }

    // Validate that accountIdToUse is a valid ID (not a boolean or other non-string type)
    // This prevents issues where accountId=true appears in URLs
    if (typeof accountIdToUse !== 'string' && typeof accountIdToUse !== 'number') {
      accountIdToUse = null;
    }

    return accountIdToUse;
  }, [selectedAccountId, user, isAdmin, isSuperAdmin]);

  // Check if an account selection is required
  const isAccountSelectionRequired = useCallback(() => {
    if (!user) return false;

    // Regular admins need to select an account
    const required = isAdmin(user) && !isSuperAdmin(user) && !selectedAccountId;
    return required;
  }, [selectedAccountId, user]);

  // Fetch accounts when the user changes, but only for regular admins
  useEffect(() => {
    if (user) {
      // Only fetch accounts for regular admins (not super admins or regular users)
      if (isAdmin(user) && !isSuperAdmin(user)) {
        console.log('User changed, fetching accounts for regular admin');
        fetchAccounts();
      } else {
        console.log('User changed but not a regular admin, skipping account fetch');
        // If not a regular admin, unblock chat history loading
        if (window.blockInitialChatHistoryCall !== undefined) {
          window.blockInitialChatHistoryCall = false;
        }
      }
    }
  }, [user, fetchAccounts]);


  // Listen for logout, login, and impersonation events
  useEffect(() => {
    const handleLogout = () => {
      setSelectedAccountId(null);
      setAccounts([]);
      setAccountsFetched(false);
      setUser(null);
    };

    const handleLogin = (event) => {
      const { user: newUser } = event.detail;

      // Reset account state
      setSelectedAccountId(null);
      setAccounts([]);
      setAccountsFetched(false);

      // Set a flag to prevent race conditions with chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = true;
      }

      // Set the new user
      setUser(newUser);

      // If the user is a regular admin, fetch accounts
      if (newUser && isAdmin(newUser) && !isSuperAdmin(newUser)) {
        fetchAccountsForUser(newUser);
      }
    };

    const handleImpersonation = (event) => {
      const { impersonatedUser } = event.detail;

      console.log('Handling impersonation event with user:', impersonatedUser);

      // Reset accounts but don't clear selectedAccountId
      // This allows the selectedAccountId to persist during impersonation
      setAccounts([]);
      setAccountsFetched(false);

      // Set a flag to prevent race conditions with chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = true;
      }

      // Reset the fetching flag to allow new requests
      isFetchingAccountsRef.current = false;

      // Set the new user
      setUser(impersonatedUser);

      // If the impersonated user is a regular admin, fetch their accounts
      if (impersonatedUser && isAdmin(impersonatedUser) && !isSuperAdmin(impersonatedUser)) {
        console.log('Impersonated user is a regular admin, fetching accounts');

        // Use setTimeout to ensure the user state is updated before fetching accounts
        setTimeout(() => {
          fetchAccountsForUser(impersonatedUser);
        }, 100);
      } else {
        console.log('Impersonated user is not a regular admin or is a super admin');
        // If not a regular admin, unblock chat history loading
        if (window.blockInitialChatHistoryCall !== undefined) {
          window.blockInitialChatHistoryCall = false;
        }
      }
    };

    const handleEndImpersonation = (event) => {
      const { originalUser } = event.detail;

      console.log('Handling end impersonation event with user:', originalUser);

      // Reset accounts but don't clear selectedAccountId
      // This allows the selectedAccountId to persist when ending impersonation
      setAccounts([]);
      setAccountsFetched(false);

      // Set a flag to prevent race conditions with chat history loading
      if (window.blockInitialChatHistoryCall !== undefined) {
        window.blockInitialChatHistoryCall = true;
      }

      // Reset the fetching flag to allow new requests
      isFetchingAccountsRef.current = false;

      // Set the original user
      setUser(originalUser);

      // If the original user is a regular admin, fetch their accounts
      if (originalUser && isAdmin(originalUser) && !isSuperAdmin(originalUser)) {
        console.log('Original user is a regular admin, fetching accounts');

        // Use setTimeout to ensure the user state is updated before fetching accounts
        setTimeout(() => {
          fetchAccountsForUser(originalUser);
        }, 100);
      } else {
        console.log('Original user is not a regular admin or is a super admin');
        // If not a regular admin, unblock chat history loading
        if (window.blockInitialChatHistoryCall !== undefined) {
          window.blockInitialChatHistoryCall = false;
        }
      }
    };

    window.addEventListener('userLogout', handleLogout);
    window.addEventListener('userLogin', handleLogin);
    window.addEventListener('userImpersonation', handleImpersonation);
    window.addEventListener('endUserImpersonation', handleEndImpersonation);

    return () => {
      window.removeEventListener('userLogout', handleLogout);
      window.removeEventListener('userLogin', handleLogin);
      window.removeEventListener('userImpersonation', handleImpersonation);
      window.removeEventListener('endUserImpersonation', handleEndImpersonation);
    };
  }, [fetchAccountsForUser]);

  // No longer registering with apiConfig since we've removed the interceptor

  // Fetch the current account's name from a per-account endpoint that any
  // account member can hit. Optimistically populate from the existing
  // `accounts[]` list when it's available (regular-admin role) to avoid an
  // extra round trip; otherwise call the API. Cached per-accountId so we
  // don't refetch on every render.
  const fetchCurrentAccountName = useCallback(async (accountIdToUse) => {
    if (!accountIdToUse) {
      setCurrentAccountName(null);
      fetchedNameForRef.current = null;
      return;
    }
    if (fetchedNameForRef.current === String(accountIdToUse)) return; // already in cache
    // Optimistic hit from the already-loaded accounts list (admin path).
    const fromList = accounts.find((a) => String(a.id) === String(accountIdToUse))?.name;
    if (fromList) {
      setCurrentAccountName(fromList);
      fetchedNameForRef.current = String(accountIdToUse);
      return;
    }
    const userData = getUserData();
    const userId = userData?.userId || userData?.id;
    if (!userId) return;
    try {
      const res = await api.get(getApiUrl(`/api/account-admin/accounts/${accountIdToUse}`), {
        params: { userId, accountId: accountIdToUse },
      });
      if (res.data?.name) {
        setCurrentAccountName(res.data.name);
        fetchedNameForRef.current = String(accountIdToUse);
      }
    } catch (e) {
      // 403 (not a member) or 404 (account removed) — leave name unset so
      // name-based gates fail closed rather than spuriously matching.
      console.warn('Failed to fetch current account name:', e?.response?.data?.error || e.message);
      setCurrentAccountName(null);
      fetchedNameForRef.current = String(accountIdToUse); // avoid retry storm
    }
  }, [accounts]);

  // Refresh the name whenever the selected account or the accounts list
  // changes (the list change lets us promote a cached name without a fetch).
  useEffect(() => {
    if (!user) return;
    const accountIdToUse = getAccountIdToUse();
    fetchCurrentAccountName(accountIdToUse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedAccountId, accounts]);

  // Reset account state (used when logging out)
  const resetAccountState = useCallback(() => {
    setSelectedAccountId(null);
    setAccounts([]);
    setAccountsFetched(false);
    setUser(null);
    setCurrentAccountName(null);
    fetchedNameForRef.current = null;
  }, []);

  // Provide the context value
  const contextValue = {
    selectedAccountId,
    accounts,
    isLoading,
    setIsLoading,
    selectAccount,
    clearSelectedAccount,
    fetchAccounts,
    getAccountIdToUse,
    isAccountSelectionRequired,
    user,
    detectPageRefresh,
    resetAccountState,
    // Account-name source-of-truth for non-admin roles. Use this for any
    // gate that needs the name (e.g. Review CTS / Launch Template button).
    currentAccountName,
  };

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  );
};

// Custom hook to use the account context
export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

export default AccountContext;
